import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

// Create the Form Change Context
const FormChangeContext = createContext();

/**
 * FormChangeProvider component that manages form change detection and state management
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const FormChangeProvider = ({ children }) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [originalFormState, setOriginalFormState] = useState(null);


  /**
   * Set the original form state to compare against
   */
  const setOriginalState = useCallback((formData) => {
    setOriginalFormState(JSON.parse(JSON.stringify(formData))); // Deep clone
    setHasChanges(false);
  }, []);

  /**
   * Compare current form state with original and update hasChanges
   * Uses JSON.stringify for reliable deep comparison of serializable objects
   */
  const checkForChanges = useCallback(
    (currentFormData) => {
      if (!originalFormState) {
        setHasChanges(false);
        return;
      }

      
      const hasActualChanges = JSON.stringify(originalFormState) !== JSON.stringify(currentFormData);
      setHasChanges(hasActualChanges);
    },
    [originalFormState]
  );

  /**
   * Mark that changes have been saved (reset to new original state)
   */
  const markAsSaved = useCallback(
    (newFormData) => {
      if (newFormData) {
        setOriginalState(newFormData);
      } else {
        setHasChanges(false);
      }
    },
    [setOriginalState]
  );

  // Context value object
  const value = {
    hasChanges,
    setOriginalState,
    checkForChanges,
    markAsSaved,
  };

  return <FormChangeContext.Provider value={value}>{children}</FormChangeContext.Provider>;
};

/**
 * Custom hook to use the Form Change Context
 * @returns {Object} Form change context value
 * @throws {Error} If used outside of FormChangeProvider
 */
export const useFormChange = () => {
  const context = useContext(FormChangeContext);

  if (context === undefined) {
    throw new Error("useFormChange must be used within a FormChangeProvider");
  }

  return context;
};

export default FormChangeContext;
